import { Chip } from "components/DesignSystem/Chips/Chips";
import { UpdateFilterFunction } from "hooks/useFilter";
import { useMemo } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import {
  RULE_STATE,
  RULE_SOURCE,
  RuleStateType,
  RuleSourceType,
} from "constants/ruleEngine";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Filter } from "components/DesignSystem/Filter/Filter";

export type RuleEngineFilterType = {
  STATE: RuleStateType | null;
  RULE_SOURCE: RuleSourceType | null;
};

const StateFilter = ({
  filter,
  updateFilter,
}: {
  filter: RuleEngineFilterType;
  updateFilter: UpdateFilterFunction<RuleEngineFilterType>;
}) => {
  return (
    <span className="t-flex t-flex-col">
      <RadioGroup.Root
        value={filter.STATE ?? ""}
        onValueChange={(v: RuleStateType) => {
          updateFilter("STATE", v);
        }}
      >
        <RadioGroup.Content>
          <RadioGroup.Item value={RULE_STATE.ACTIVE} className="!t-h-auto">
            Active
          </RadioGroup.Item>
          <RadioGroup.Item value={RULE_STATE.DISABLED} className="!t-h-auto">
            Disabled
          </RadioGroup.Item>
        </RadioGroup.Content>
      </RadioGroup.Root>
    </span>
  );
};

const RuleTypeFilter = ({
  filter,
  updateFilter,
}: {
  filter: RuleEngineFilterType;
  updateFilter: UpdateFilterFunction<RuleEngineFilterType>;
}) => {
  return (
    <span className="t-flex t-flex-col">
      <RadioGroup.Root
        value={filter.RULE_SOURCE ?? ""}
        onValueChange={(v: RuleSourceType) => {
          updateFilter("RULE_SOURCE", v);
        }}
      >
        <RadioGroup.Content>
          <RadioGroup.Item value={RULE_SOURCE.MANUAL} className="!t-h-auto">
            User generated
          </RadioGroup.Item>
          <RadioGroup.Item value={RULE_SOURCE.SYSTEM} className="!t-h-auto">
            System generated
          </RadioGroup.Item>
        </RadioGroup.Content>
      </RadioGroup.Root>
    </span>
  );
};

export const RuleEngineFilter = ({
  filter,
  updateFilter,
}: {
  filter: RuleEngineFilterType;
  updateFilter: UpdateFilterFunction<RuleEngineFilterType>;
}) => {
  const appliedFilterCount = useMemo(
    () =>
      Object.keys(filter).reduce((acc, key) => {
        if (filter[key as keyof RuleEngineFilterType]) {
          return acc + 1;
        }
        return acc;
      }, 0),
    [filter]
  );

  return (
    <Filter.Root
      defaultValue="state"
      title={
        appliedFilterCount > 0 ? (
          <span className="t-text-text-30">({appliedFilterCount})</span>
        ) : null
      }
      capsule={
        <>
          <Chip
            onClick={() => {
              updateFilter("STATE", RULE_STATE.ACTIVE);
            }}
            onRemove={() => {
              updateFilter("STATE", null);
            }}
            isRemovable={true}
            isFixedFilter={true}
            isActive={filter.STATE === RULE_STATE.ACTIVE}
            filterType="state"
          >
            Active
          </Chip>
          {filter.STATE === RULE_STATE.DISABLED && (
            <Chip
              key="disabled"
              onRemove={() => {
                updateFilter("STATE", null);
              }}
              isRemovable={true}
              isActive
              filterType="state"
            >
              Disabled
            </Chip>
          )}

          {filter.RULE_SOURCE === RULE_SOURCE.MANUAL && (
            <Chip
              key="user-generated"
              onRemove={() => {
                updateFilter("RULE_SOURCE", null);
              }}
              isRemovable={true}
              isActive
              filterType="ruleSource"
            >
              User generated
            </Chip>
          )}

          {filter.RULE_SOURCE === RULE_SOURCE.SYSTEM && (
            <Chip
              key="system-generated"
              onRemove={() => {
                updateFilter("RULE_SOURCE", null);
              }}
              isRemovable={true}
              isActive
              filterType="ruleSource"
            >
              System generated
            </Chip>
          )}
        </>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value="state">State</Filter.ListItem>
          <Filter.ListItem value="ruleSource">Rule Type</Filter.ListItem>
        </Filter.List>

        <Filter.Body value="state">
          <StateFilter filter={filter} updateFilter={updateFilter} />
        </Filter.Body>

        <Filter.Body value="ruleSource">
          <RuleTypeFilter filter={filter} updateFilter={updateFilter} />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
